/**
 * SavedSearchesPage.jsx - SIMPLE ET PROPRE
 *
 * ✅ Logo Discord avec react-icons (FaDiscord)
 * ✅ Switch simple et centré
 * ✅ Boutons inchangés
 * ✅ Design minimaliste
 *
 * Installation requise:
 * npm install react-icons
 */

import React, { useEffect, useState } from "react";
import { getSavedSearches, deleteSavedSearch, updateSavedSearch } from "../api/api";
import { useNavigate } from "react-router-dom";
import { List, Button, Space, Empty, Spin, Popconfirm, message, Switch } from "antd";
import { BellOutlined, DeleteOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { FaDiscord } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";
import AdvancedSearchFilters from "../components/Search/AdvancedSearchFilter";

/**
 * SavedSearchesPage - Recherches sauvegardées
 */
export default function SavedSearchesPage() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [searches, setSearches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingSearchId, setEditingSearchId] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [toggling, setToggling] = useState(null);

    const fetchSearches = async () => {
        try {
            setLoading(true);
            const res = await getSavedSearches();
            setSearches(res.data.searches || []);
        } catch (err) {
            console.error(err);
            message.error("Erreur chargement recherches");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSearches();
    }, []);

    /**
     * Lance une recherche sauvegardée → /search?filters=...
     */
    const launchSearch = (filters) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) return;

            if (Array.isArray(value)) {
                value.forEach((v) => {
                    if (v) params.append(`${key}[]`, v);
                });
            } else {
                params.set(key, value);
            }
        });

        navigate(`/search?${params.toString()}`);
    };

    /**
     * ✅ Toggle Discord notifications
     */
    const handleDiscordToggle = async (searchId, newValue) => {
        try {
            setToggling(searchId);
            await updateSavedSearch(searchId, { is_active: newValue });

            setSearches((prev) =>
                prev.map((s) =>
                    s.id === searchId ? { ...s, is_active: newValue } : s
                )
            );

            message.success(newValue ? "Notifications Discord activées ✅" : "Notifications Discord désactivées");

        } catch (err) {
            console.error(err);
            message.error("Erreur mise à jour");
            fetchSearches();
        } finally {
            setToggling(null);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteSavedSearch(id);
            message.success("Recherche supprimée");
            fetchSearches();
        } catch (err) {
            console.error(err);
            message.error("Erreur suppression");
        }
    };

    const handleViewAlerts = (searchId) => {
        navigate(`/alerts/${searchId}`);
    };

    const handleEditSearch = (search) => {
        launchSearch(search.filters);
        setEditingSearchId(search.id);
        setEditModalOpen(true);
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "48px" }}>
                <Spin />
            </div>
        );
    }

    if (searches.length === 0) {
        return (
            <div style={{ padding: "24px", minHeight: "100vh", background: isDarkMode ? "#000000" : "#fafafa" }}>
                <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "24px" }}>
                    Mes recherches sauvegardées
                </h2>
                <Empty description="Aucune recherche sauvegardée" style={{ marginTop: "48px" }} />
            </div>
        );
    }

    const editingSearch = searches.find((s) => s.id === editingSearchId);

    return (
        <div style={{ padding: "24px", minHeight: "100vh", background: isDarkMode ? "#000000" : "#fafafa" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "24px" }}>
                    Mes recherches sauvegardées
                </h2>

                <List
                    dataSource={searches}
                    renderItem={(search) => (
                        <List.Item
                            style={{
                                background: isDarkMode ? "#1f1f1f" : "#ffffff",
                                padding: "16px",
                                marginBottom: "12px",
                                borderRadius: "6px",
                                border: `1px solid ${isDarkMode ? "#434343" : "#d9d9d9"}`,
                            }}
                        >
                            {/* Gauche: Titre + Description */}
                            <div style={{ flex: 1 }}>
                                <List.Item.Meta
                                    title={<strong>{search.name}</strong>}
                                    description={
                                        <div style={{ fontSize: "12px", opacity: 0.6 }}>
                                            {search.created_at && (
                                                <span>
                                                    Créée le{" "}
                                                    {new Date(search.created_at).toLocaleDateString("fr-FR")}
                                                </span>
                                            )}
                                        </div>
                                    }
                                />
                            </div>

                            {/* Milieu: Discord Toggle */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    marginRight: "16px",
                                }}
                            >
                                <FaDiscord
                                    size={20}
                                    color={search.is_active ? "#5865F2" : "#999999"}
                                    title={search.is_active ? "Discord activé" : "Discord désactivé"}
                                />
                                <Switch
                                    checked={search.is_active}
                                    onChange={(value) => handleDiscordToggle(search.id, value)}
                                    loading={toggling === search.id}
                                />
                            </div>

                            {/* Droite: Boutons (INCHANGÉS) */}
                            <Space>
                                {/* Bouton Lancer */}
                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={() => launchSearch(search.filters)}
                                >
                                    Lancer
                                </Button>

                                {/* Bouton Alertes */}
                                <Button
                                    icon={<BellOutlined />}
                                    onClick={() => handleViewAlerts(search.id)}
                                >
                                    Alertes
                                </Button>

                                {/* Bouton Supprimer */}
                                <Popconfirm
                                    title="Supprimer la recherche ?"
                                    description="Cette action est irréversible"
                                    onConfirm={() => handleDelete(search.id)}
                                    okText="Oui"
                                    cancelText="Non"
                                >
                                    <Button danger icon={<DeleteOutlined />}>
                                        Supprimer
                                    </Button>
                                </Popconfirm>
                            </Space>
                        </List.Item>
                    )}
                />
            </div>

            {/* Modal d'édition */}
            {editingSearch && (
                <AdvancedSearchFilters
                    initialFilters={editingSearch.filters}
                    isEditing={true}
                    searchId={editingSearchId}
                    searchName={editingSearch.name}
                    onSaveSuccess={() => {
                        message.success("Recherche mise à jour");
                        setEditModalOpen(false);
                        setEditingSearchId(null);
                        fetchSearches();
                    }}
                />
            )}
        </div>
    );
}